import React, { useRef, useState } from 'react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Download, Image, Loader2 } from 'lucide-react';
import { Contribution, Expense } from '../../types';
import { exportChartAsImage } from '../../utils/chartExport';

interface FinancialSummaryChartProps {
  contributions: Contribution[];
  expenses: Expense[];
  chartType?: 'line' | 'area' | 'bar';
  timeRange?: 'monthly' | 'weekly' | 'daily';
  height?: number;
}

export const FinancialSummaryChart: React.FC<FinancialSummaryChartProps> = ({
  contributions,
  expenses,
  chartType = 'line',
  timeRange = 'monthly',
  height = 300
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);

  const processedData = React.useMemo(() => {
    const allTransactions = [
      ...contributions.map(c => ({ ...c, type: 'income', date: c.date })),
      ...expenses.map(e => ({ ...e, type: 'expense', date: e.date }))
    ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const grouped = allTransactions.reduce((acc, transaction) => {
      const date = new Date(transaction.date);
      let key: string;

      if (timeRange === 'daily') {
        key = date.toISOString().split('T')[0];
      } else if (timeRange === 'weekly') {
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        key = weekStart.toISOString().split('T')[0];
      } else {
        key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      }

      if (!acc[key]) {
        acc[key] = { period: key, income: 0, expense: 0 };
      }

      if (transaction.type === 'income') {
        acc[key].income += transaction.amount;
      } else {
        acc[key].expense += transaction.amount;
      }

      return acc;
    }, {} as Record<string, { period: string; income: number; expense: number }>);

    return Object.values(grouped).map(item => ({
      ...item,
      net: item.income - item.expense,
      periodLabel: timeRange === 'daily'
        ? new Date(item.period).toLocaleDateString()
        : timeRange === 'weekly'
          ? `Week of ${new Date(item.period).toLocaleDateString()}`
          : new Date(item.period + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'short' })
    }));
  }, [contributions, expenses, timeRange]);

  const handleExportImage = async () => {
    if (!chartRef.current) return;

    setIsExporting(true);
    try {
      // Add a temporary ID to the chart container for export
      const tempId = `financial-chart-${Date.now()}`;
      chartRef.current.id = tempId;

      await exportChartAsImage(tempId, 'financial_summary_chart');

      // Clean up
      chartRef.current.id = '';
    } catch (error) {
      console.error('Failed to export chart:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{`${label}`}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }}>
              {`${entry.name}: GHS ${entry.value?.toFixed(2)}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    const commonProps = {
      data: processedData,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    };

    switch (chartType) {
      case 'area':
        return (
          <AreaChart {...commonProps}>
            <defs>
              <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#00C49F" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#00C49F" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorExpense" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#FF8042" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#FF8042" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <XAxis dataKey="periodLabel" />
            <YAxis />
            <CartesianGrid strokeDasharray="3 3" />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Area type="monotone" dataKey="income" stackId="1" stroke="#00C49F" fillOpacity={1} fill="url(#colorIncome)" name="Income" />
            <Area type="monotone" dataKey="expense" stackId="1" stroke="#FF8042" fillOpacity={1} fill="url(#colorExpense)" name="Expenses" />
          </AreaChart>
        );

      case 'bar':
        return (
          <BarChart {...commonProps}>
            <XAxis dataKey="periodLabel" />
            <YAxis />
            <CartesianGrid strokeDasharray="3 3" />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="income" fill="#00C49F" name="Income" />
            <Bar dataKey="expense" fill="#FF8042" name="Expenses" />
            <Bar dataKey="net" fill="#0088FE" name="Net Balance" />
          </BarChart>
        );

      default: // line
        return (
          <LineChart {...commonProps}>
            <XAxis dataKey="periodLabel" />
            <YAxis />
            <CartesianGrid strokeDasharray="3 3" />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line type="monotone" dataKey="income" stroke="#00C49F" strokeWidth={2} name="Income" />
            <Line type="monotone" dataKey="expense" stroke="#FF8042" strokeWidth={2} name="Expenses" />
            <Line type="monotone" dataKey="net" stroke="#0088FE" strokeWidth={2} name="Net Balance" />
          </LineChart>
        );
    }
  };

  const totals = React.useMemo(() => ({
    income: contributions.reduce((sum, c) => sum + c.amount, 0),
    expenses: expenses.reduce((sum, e) => sum + e.amount, 0),
    net: contributions.reduce((sum, c) => sum + c.amount, 0) - expenses.reduce((sum, e) => sum + e.amount, 0)
  }), [contributions, expenses]);

  return (
    <div className="w-full">
      <div className="flex justify-end mb-4 space-x-2">
        <button
          onClick={handleExportImage}
          disabled={isExporting}
          className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-colors"
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Image className="h-4 w-4" />
          )}
          <span>Export Chart</span>
        </button>
      </div>

      <div ref={chartRef}>
        <ResponsiveContainer width="100%" height={height}>
          {renderChart()}
        </ResponsiveContainer>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
        <div className="text-center">
          <p className="text-green-600 font-semibold">
            GHS {totals.income.toFixed(2)}
          </p>
          <p className="text-gray-600">Total Income</p>
        </div>
        <div className="text-center">
          <p className="text-red-600 font-semibold">
            GHS {totals.expenses.toFixed(2)}
          </p>
          <p className="text-gray-600">Total Expenses</p>
        </div>
        <div className="text-center">
          <p className={`font-semibold ${
            totals.net >= 0 ? 'text-green-600' : 'text-red-600'
          }`}>
            GHS {totals.net.toFixed(2)}
          </p>
          <p className="text-gray-600">Net Balance</p>
        </div>
      </div>
    </div>
  );
};